import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './ArchitectsBlueprintPage.css';

// Warm Firebase callable so MicroAssessmentPage loads instantly
let _prefetchedTTS = null;
function prefetchTTS() {
  if (!_prefetchedTTS) {
    try {
      const fns = getFunctions();
      _prefetchedTTS = httpsCallable(fns, 'openaiTTS');
    } catch { /* non-fatal */ }
  }
}

const MODULES = [
  {
    id: 'synthesis',
    tier: 'foundations',
    title: 'Foundations',
    type: 'Define / Execute',
    summary: 'The prep course for Full Operator. Closes gaps in brief construction, AI direction, and output diagnosis — so when you step into Operator, nothing slows you down.',
    detail:
      'Recommended when the assessment identifies foundational gaps. The learner builds structured briefs, directs AI with precision, and diagnoses what came back — establishing the control that Full Operator builds on.',
    checkpoints: [
      { text: 'AI tools defined — strengths, weaknesses, and the right tool for the right task' },
      { text: 'Observe AI instructor demonstrate correct brief construction and execution' },
      { text: 'Interact with AI to build and execute your own brief on a real task' },
      { text: 'Demonstrate the technique back to the AI instructor — validate why it worked' },
    ],
  },
  {
    id: 'ethics',
    tier: 'operator',
    title: 'Operator',
    type: 'Sequence / Optimize',
    summary: 'Chain prompts into multi-step workflows. Build sequences that hold under real conditions, adapt as complexity increases, and document a playbook you can repeat.',
    detail:
      'The end goal is workflow command. The learner turns isolated prompts into repeatable sequences that hold under added constraints, variation, and real-world pressure.',
    checkpoints: [
      { text: 'Workflow patterns defined — how steps chain, compound, and hold under pressure' },
      { text: 'Observe AI instructor build and execute a multi-step workflow sequence' },
      { text: 'Interact with AI to construct and run your own workflow on a recurring task' },
      { text: 'Walk the AI instructor through your workflow logic — validate why each step holds' },
    ],
  },
  {
    id: 'streams',
    tier: 'certified',
    title: 'Certified Operator',
    type: 'Construct / Deploy',
    summary: 'Architect AI systems, identify where they fail, and design fixes others can follow. You ship a functioning app you keep when the course ends.',
    detail:
      'The end goal is strategic leadership. The learner can teach the principles, see where AI systems break in their own environment, and direct stronger structures around those limits.',
    checkpoints: [
      { text: 'System design principles defined — where AI structures succeed and where they fail' },
      { text: 'Observe AI instructor architect a complete system under real-world constraints' },
      { text: 'Interact with AI to design and validate your own operational system' },
      { text: 'Ship a functioning app — a working system you build and keep upon completion' },
    ],
    credential: {
      title: 'Certified Operator Designation',
      description: 'Display on LinkedIn & professional signature blocks',
      icon: '✓✓✓',
    },
  },
];

const TIER_VISUALS = {
  foundations: {
    border: 'rgba(57, 213, 255, 0.38)',
    glow: 'rgba(57, 213, 255, 0.15)',
    node: '#39d5ff',
    bgStart: 'rgba(7, 20, 42, 0.92)',
    bgEnd: 'rgba(5, 12, 28, 0.72)',
  },
  operator: {
    border: 'rgba(57, 213, 255, 0.56)',
    glow: 'rgba(57, 213, 255, 0.25)',
    node: '#39d5ff',
    bgStart: 'rgba(8, 24, 50, 0.92)',
    bgEnd: 'rgba(5, 14, 32, 0.72)',
  },
  certified: {
    border: 'rgba(57, 213, 255, 0.76)',
    glow: 'rgba(57, 213, 255, 0.38)',
    node: '#39d5ff',
    bgStart: 'rgba(10, 28, 58, 0.92)',
    bgEnd: 'rgba(7, 18, 38, 0.72)',
  },
};

const BASE_LOGS = [
  '[NEURAL_NEXUS]: SIMULATION DETECTED. ALLOCATING_RESOURCES...',
  '[USER_INTERACTION]: ENHANCING INTERFACE DYNAMICS.',
  '[LOG]: OPTIMIZING HUMAN LEARNING CURVE... 89.4%',
  '[SYNTHESIS]: MAPPING CONTEXT -> CONSTRAINTS -> OUTCOME',
  '[ETHICS]: CALIBRATING TRADEOFF MATRIX FOR HUMAN VALUES',
];

const SPINNER_MESSAGES = [
  'DETECTING FOCUS VECTORS',
  'RENDERING DECISION TOPOLOGY',
  'INDEXING KNOWLEDGE NODES',
  'CALCULATING COGNITIVE LOAD',
  'ALIGNING HUMAN POTENTIAL GRAPH',
  'OPTIMIZING LESSON COMPLEXITY',
];

/* Funnel sequence removed — registration happens post-assessment, not on landing page */

function makeLogLine() {
  const now = new Date();
  const time = now.toTimeString().slice(0, 8);
  const message = SPINNER_MESSAGES[Math.floor(Math.random() * SPINNER_MESSAGES.length)];
  return `[${time}] [NEURAL_NEXUS]: ${message}.`;
}

export default function ArchitectsBlueprintPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [cursor, setCursor] = useState({ x: 0.5, y: 0.5 });
  const [activeModule, setActiveModule] = useState(MODULES[0].id);
  const [completedModules, setCompletedModules] = useState([MODULES[0].id]);
  const [terminalHot, setTerminalHot] = useState(false);
  const [socketLoaded, setSocketLoaded] = useState(false);
  const [logs, setLogs] = useState(() => [...BASE_LOGS]);

  const completion = completedModules.length / MODULES.length;

  const currentModule = useMemo(
    () => MODULES.find((module) => module.id === activeModule) || MODULES[0],
    [activeModule],
  );

  const currentTierVisual = TIER_VISUALS[currentModule.tier] || TIER_VISUALS.foundations;

  useEffect(() => {
    const interval = setInterval(() => {
      setLogs((prev) => [...prev.slice(-23), makeLogLine()]);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Prefetch TTS callable while visitor reads the landing page
  useEffect(() => {
    const timer = setTimeout(prefetchTTS, 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setSocketLoaded(true), 2800);
    return () => clearTimeout(timer);
  }, []);

  function advanceForMoreInfo() {
    setTerminalHot(true);
    setLogs((prev) => [
      ...prev.slice(-23),
      '[USER_INTERACTION]: ADVANCE REQUESTED -> MICRO_ASSESSMENT ENTRY.',
    ]);
    setTimeout(() => setTerminalHot(false), 900);
    navigate('/micro-assessment', {
      state: {
        blueprintProgress: completion,
        activeTier: currentModule.tier,
        selectedModuleId: activeModule,
        viewedModules: completedModules,
      },
    });
  }

  function setFocus(moduleId) {
    setActiveModule(moduleId);
    setCompletedModules((prev) => (prev.includes(moduleId) ? prev : [...prev, moduleId]));
    setTerminalHot(true);
    setLogs((prev) => [
      ...prev.slice(-23),
      `[USER_INTERACTION]: FOCUS SHIFT -> ${moduleId.toUpperCase()} NODE.`,
    ]);
    setTimeout(() => setTerminalHot(false), 700);
  }

  return (
    <main
      className="abp-page"
      style={{
        '--mouse-x': cursor.x,
        '--mouse-y': cursor.y,
        '--completion': completion,
      }}
      onMouseMove={(event) => {
        const nextX = event.clientX / window.innerWidth;
        const nextY = event.clientY / window.innerHeight;
        setCursor({ x: nextX, y: nextY });
      }}
    >
      <div className="abp-layout">
        <section className="abp-main">
          <article className="abp-panel abp-hero">
            <div className="abp-hero-topbar">
              <div className={`abp-origin ${socketLoaded ? 'is-loaded' : ''}`}>
                <span className={`abp-origin-dot ${socketLoaded ? 'is-loaded' : ''}`} />
                <span>{socketLoaded ? 'Loaded API socket: now open.' : 'Port assignment'}</span>
              </div>
              {!currentUser && (
                <button
                  className="abp-origin-login"
                  onClick={() => navigate('/login')}
                >
                  Login
                </button>
              )}
            </div>
            <div className="abp-hero-grid">
              <div>
                <h1 className="abp-title">You Know How to Talk to AI. Yet the Results Never Match the Effort.</h1>
                <p className="abp-subtitle">
                  You are not alone — and it is not your fault. There is a method to this.
                </p>
              </div>

            </div>
          </article>

          <article className="abp-panel abp-transition-card">
            <div className="abp-transition-head">
              <h2>Sound Familiar?</h2>
              <p>
                You get responses — but they are weak, generic, and inconsistent. You rewrite the same prompt
                three or four times and still end up with output you would never put your name on. Millions of
                people are stuck in the exact same loop.
              </p>
            </div>

            <div className="abp-transition-grid">
              <div className="abp-transition-node">
                <span className="abp-transition-tag">The Pattern</span>
                <p>
                  You ask AI for something specific. It gives you something close but not strong enough to use.
                  You add more detail, try again, and the result barely improves. So you start over.
                </p>
              </div>

              <div className="abp-transition-node">
                <span className="abp-transition-tag">The Real Problem</span>
                <p>
                  It is not that AI cannot do the work. It is that nobody taught you how to direct it.
                  The gap is not in the technology — it is in the instruction layer between you and the machine.
                </p>
              </div>

              <div className="abp-transition-node">
                <span className="abp-transition-tag">You Are Not Behind</span>
                <p>
                  Most AI users are in this exact position. They can get a response but they cannot get a result.
                  That is the difference — and it is completely fixable.
                </p>
              </div>
            </div>
          </article>

          <article className="abp-panel abp-transition-card abp-transition-card-secondary">
            <div className="abp-transition-head">
              <h2>We Built the Solution.</h2>
              <p>
                The Operator Method is a structured AI training program that teaches you how to direct AI —
                not just talk to it. Stronger prompts. Consistent results. Real output you can use.
              </p>
            </div>

            <div className="abp-readiness-track" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>

            <div className="abp-transition-grid">
              <div className="abp-transition-node">
                <span className="abp-transition-tag">Find Your Level</span>
                <p>A free assessment identifies exactly where you are so the training starts at your real leverage point — not page one of a generic course.</p>
              </div>

              <div className="abp-transition-node abp-transition-node-highlight">
                <span className="abp-transition-tag abp-transition-tag-highlight">What Sets This Apart</span>
                <p>
                  This is not a video library. You train with a <strong className="abp-highlight-text">LIVE AI INSTRUCTOR</strong> that reads your skill level in real time, demonstrates the method, critiques your work, and advances you when you prove competency. No other course does this.
                </p>
              </div>

              <div className="abp-transition-node">
                <span className="abp-transition-tag">The Guarantee</span>
                <p>Measurably stronger AI results within two weeks — or your money back. No hedging, no fine print. If the method does not work for you, you pay nothing.</p>
                <p className="abp-highlight-method">
                  The method: SEE IT. DO IT. TEACH IT.
                </p>
              </div>
            </div>
          </article>

          <article className="abp-panel abp-learn">
            <div className="abp-learn-header">
              <div>
                <h2>The Learning Core</h2>
                  <p>Every module is led by a <strong className="abp-highlight-text">LIVE AI INSTRUCTOR</strong> that adapts to you — reads your current competency, identifies gaps, and only advances you when you demonstrate the skill. Not pre-recorded. Not self-paced guesswork. Live, responsive, and built around your progress.</p>
                  <p className="abp-learn-levels">The course has two primary tracks — <strong>Full Operator</strong> and <strong>Certified Operator</strong>. If the assessment identifies foundational gaps, <strong>Foundations</strong> is recommended as prep before Operator. Select a track below to see what it covers.</p>
              </div>
            </div>

            <div className="abp-graph">
              <div className="abp-graph-lines" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>

              {MODULES.map((module) => {
                const isActive = module.id === activeModule;
                const isComplete = completedModules.includes(module.id);
                const moduleVisual = TIER_VISUALS[module.tier] || TIER_VISUALS.foundations;
                return (
                  <button
                    key={module.id}
                    className={`abp-node ${isActive ? 'is-active' : ''} ${isComplete ? 'is-complete' : ''}`}
                    onClick={() => setFocus(module.id)}
                    style={{
                      '--abp-node-tier-border': moduleVisual.border,
                      '--abp-node-tier-glow': moduleVisual.glow,
                      '--abp-node-tier-node': moduleVisual.node,
                    }}
                    type="button"
                  >
                    <h3>{module.title}</h3>
                    <p>{module.summary}</p>
                    <span className="abp-node-meta">{module.type}</span>
                  </button>
                );
              })}
            </div>

            <div
              className="abp-detail-bridge"
              aria-hidden="true"
              style={{
                '--abp-tier-bridge-line': currentTierVisual.border,
                '--abp-tier-bridge-glow': currentTierVisual.glow,
                '--abp-tier-bridge-node': currentTierVisual.node,
              }}
            >
              {MODULES.map((module) => (
                <span
                  key={`${module.id}-bridge`}
                  className={`abp-detail-bridge-slot ${module.id === activeModule ? 'is-active' : ''}`}
                />
              ))}
            </div>

            <div
              key={activeModule}
              className="abp-detail"
              style={{
                '--abp-tier-detail-border': currentTierVisual.border,
                '--abp-tier-detail-glow': currentTierVisual.glow,
                '--abp-tier-detail-start': currentTierVisual.bgStart,
                '--abp-tier-detail-end': currentTierVisual.bgEnd,
              }}
            >
              <div className="abp-detail-head">
                <h3>{currentModule.title}</h3>
              </div>
              
              {/* Checkpoints list */}
              <div className="abp-checkpoints-container">
                <ul className="abp-checkpoints-list">
                  {currentModule.checkpoints.map((checkpoint, idx) => (
                    <li key={idx} className="abp-checkpoint">
                      <span className="abp-checkpoint-marker">→</span>
                      <span className="abp-checkpoint-text">{checkpoint.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Credential badge */}
              {currentModule.tier === 'certified' && currentModule.credential && (
                <div className="abp-credential">
                  <div className="abp-credential-icon">{currentModule.credential.icon}</div>
                  <div className="abp-credential-info">
                    <div className="abp-credential-title">{currentModule.credential.title}</div>
                    <div className="abp-credential-desc">{currentModule.credential.description}</div>
                  </div>
                </div>
              )}

            </div>
          </article>

          <article className="abp-panel abp-continuation">
            <div className="abp-continuation-head">
              <h2 className="abp-continuation-title-highlight">Your Course. Your Level. Your Pace.</h2>
              <p>
                Every student enters at a different point. The Operator Method adapts to where you actually are —
                not where a generic syllabus assumes you should be.
              </p>
            </div>

            <div className="abp-continuation-brief">
              <p>
                Six targeted questions measure your current level of operational command —
                how you structure requests, interpret output, and recover when AI underdelivers.
                The answers place you into the exact tier and starting point where you will learn the fastest.
              </p>
              <p>
                No one sits through material they have already mastered. No one gets dropped into concepts
                they are not ready for. The course reshapes itself around you before the first lesson begins.
              </p>
            </div>

            <div className="abp-calibration-grid">
              <div className="abp-calibration-card">
                <div className="abp-calibration-index">01</div>
                <h3>Assess</h3>
                <p>Six questions. No trick prompts. A live AI examiner reads how you think about AI — not how much you have memorized.</p>
              </div>
              <div className="abp-calibration-card">
                <div className="abp-calibration-index">02</div>
                <h3>Place</h3>
                <p>Your answers map to a specific tier and starting point. The course locks onto your real skill floor.</p>
              </div>
              <div className="abp-calibration-card">
                <div className="abp-calibration-index">03</div>
                <h3>Begin</h3>
                <p>Training starts immediately at your level. The AI instructor meets you where you are and pushes from there.</p>
              </div>
            </div>

            <div className="abp-cta-block">
              <button type="button" className="abp-btn abp-btn-cta" onClick={advanceForMoreInfo}>
                Take the Free Assessment
              </button>
              <p className="abp-cta-sub">Completely free. No account required. No email. Just click and go.</p>
            </div>
          </article>

        </section>

        <aside className="abp-sidebar">
          <section className="abp-panel abp-terminal">
            <div className="abp-terminal-head">
              <h2>Live Data</h2>
              <span className={`abp-terminal-state ${terminalHot ? 'is-hot' : ''}`}>{terminalHot ? 'Sync burst' : 'Nominal'}</span>
            </div>
            <ul className="abp-log">
              {logs.slice(-24).map((line, index) => (
                (() => {
                  const dividerIndex = line.indexOf(']: ');
                  const label = dividerIndex !== -1 ? `${line.slice(0, dividerIndex + 1)}:` : line;
                  const message = dividerIndex !== -1 ? line.slice(dividerIndex + 3).trim() : '';
                  return (
                    <li key={`${line}-${index}`}>
                      <strong>{label}</strong>
                      {message ? ` ${message}` : ''}
                    </li>
                  );
                })()
              ))}
            </ul>
            <p className="abp-foot">This stream is synthetic but state-aware. Module interactions alter behavior in real time.</p>
          </section>
        </aside>
      </div>
    </main>
  );
}
